import { useEffect, useState } from "react";
import { api, type JarvisConfig, type JarvisHelpSuggestion, type JarvisToggleKey } from "../api.js";
import { useLocale } from "../ui.js";
import type { TranslateKey } from "../i18n.js";
import { WebCaptureControls } from "./WebCaptureControls.js";

/** Editable boolean controls (master switch handled separately). */
const CONTROL_KEYS: JarvisToggleKey[] = [
  "webAccess",
  "publicSignup",
  "newUserApproval",
  "browserControl",
  "voice",
  "providerRouter",
  "realExecution",
];

/** Hard-locked in this phase — shown OFF and non-interactive. */
const LOCKED_KEYS: JarvisToggleKey[] = [
  "powershellExecuteDesktop",
  "powershellExecuteWeb",
  "terminalExecute",
];

export function JarvisControlPanel({ token }: { token: string }) {
  const { tr } = useLocale();
  const [config, setConfig] = useState<JarvisConfig | null>(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api.jarvisConfig(token).then((res) => {
      if (!live) return;
      if (res.status === 403 || !res.data?.isJarvisAdmin) {
        setDenied(true);
        return;
      }
      if (res.data?.config) setConfig(res.data.config);
    });
    return () => {
      live = false;
    };
  }, [token]);

  async function patch(
    patchBody: Partial<Record<JarvisToggleKey, boolean>> & { desktopControl?: "off" | "readonly" },
  ) {
    setBusy(Object.keys(patchBody)[0] ?? "x");
    const res = await api.jarvisSetConfig(token, patchBody);
    if (res.ok && res.data?.config) setConfig(res.data.config);
    setBusy(null);
  }

  async function emergencyStop() {
    if (!window.confirm(tr("emergencyConfirm"))) return;
    setBusy("emergency");
    const res = await api.jarvisEmergencyStop(token);
    if (res.ok && res.data?.config) setConfig(res.data.config);
    setBusy(null);
  }

  async function resumeShikkaCeo() {
    if (!window.confirm(tr("resumeConfirm"))) return;
    await patch({ emergencyStop: false });
  }

  if (denied) {
    return (
      <section className="panel-card">
        <h2>{tr("jarvisCenter")}</h2>
        <div className="msg info">{tr("noAccess")}</div>
      </section>
    );
  }
  if (!config) {
    return (
      <section className="panel-card">
        <p className="muted">{tr("loading")}</p>
      </section>
    );
  }

  const actionsBlocked = config.emergencyStop || !config.jarvisEnabled;

  return (
    <div className={`jarvis ${config.presentationMode ? "presentation" : ""}`}>
      {config.presentationMode ? <div className="jarvis-watermark">{tr("presentationBadge")}</div> : null}

      <section className="panel-card">
        <div className="jarvis-head">
          <div>
            <h2>{tr("jarvisCenter")}</h2>
            <p className="section-desc">{tr("jarvisDesc")}</p>
          </div>
          {config.presentationMode ? <span className="badge warn">{tr("presentationBadge")}</span> : null}
        </div>

        {config.emergencyStop ? <div className="msg error jarvis-alarm">{tr("emergencyOn")}</div> : null}

        <button
          type="button"
          className="jarvis-estop"
          disabled={busy !== null || config.emergencyStop}
          onClick={() => void emergencyStop()}
        >
          ⏹ {tr("emergencyStop")}
        </button>

        {config.emergencyStop ? (
          <button
            type="button"
            className="jarvis-resume"
            disabled={busy !== null}
            onClick={() => void resumeShikkaCeo()}
          >
            {tr("resumeShikkaCeo")}
          </button>
        ) : null}

        <div className="jarvis-master">
          <label className="switch big">
            <input
              type="checkbox"
              checked={config.jarvisEnabled}
              disabled={busy !== null || config.emergencyStop}
              onChange={(e) => void patch({ jarvisEnabled: e.target.checked })}
            />
            <span>{tr("jarvisEnabled")}</span>
          </label>
        </div>
      </section>

      <section className="panel-card">
        <h3>{tr("liveStatus")}</h3>
        <p className="muted">{config.emergencyStop ? tr("emergencyOn") : !config.jarvisEnabled ? tr("jarvisOff") : tr("idle")}</p>
        <div className="jarvis-links">
          <span className="ss-btn-ghost disabled">{tr("approvalQueue")}</span>
          <span className="ss-btn-ghost disabled">{tr("actionHistory")}</span>
        </div>
      </section>

      <section className="panel-card">
        <h3>{tr("controls")}</h3>
        <div className="jarvis-grid">
          {CONTROL_KEYS.map((key) => (
            <label key={key} className="switch">
              <input
                type="checkbox"
                checked={Boolean(config[key])}
                disabled={busy !== null || (actionsBlocked && key !== "newUserApproval")}
                onChange={(e) => void patch({ [key]: e.target.checked })}
              />
              <span>{tr(key as TranslateKey)}</span>
            </label>
          ))}

          <div className="jarvis-select">
            <span>{tr("desktopControl")}</span>
            <select
              value={config.desktopControl}
              disabled={busy !== null || actionsBlocked}
              onChange={(e) => void patch({ desktopControl: e.target.value as "off" | "readonly" })}
            >
              <option value="off">{tr("dcOff")}</option>
              <option value="readonly">{tr("dcReadonly")}</option>
            </select>
          </div>

          <label className="switch">
            <input
              type="checkbox"
              checked={config.presentationMode}
              disabled={busy !== null}
              onChange={(e) => void patch({ presentationMode: e.target.checked })}
            />
            <span>{tr("presentationMode")}</span>
          </label>
        </div>

        <h4 className="jarvis-locked-title">🔒 {tr("lockedExec")}</h4>
        <div className="jarvis-grid">
          {LOCKED_KEYS.map((key) => (
            <label key={key} className="switch locked" title={tr("lockedNote")}>
              <input type="checkbox" checked={false} disabled readOnly />
              <span>{tr(key as TranslateKey)}</span>
            </label>
          ))}
        </div>
        <p className="muted">{tr("lockedNote")}</p>
      </section>

      <section className="panel-card">
        <h3>{tr("captureTitle")}</h3>
        <p className="section-desc">{tr("captureDesc")}</p>
        <WebCaptureControls />
      </section>

      <DesktopHelp token={token} />
    </div>
  );
}

function DesktopHelp({ token }: { token: string }) {
  const { tr } = useLocale();
  const [prompt, setPrompt] = useState("");
  const [shell, setShell] = useState<"powershell" | "bash">("powershell");
  const [suggestion, setSuggestion] = useState<JarvisHelpSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  async function explain() {
    if (!prompt.trim()) return;
    setLoading(true);
    const res = await api.jarvisHelp(token, prompt, shell);
    if (res.ok && res.data?.suggestion) setSuggestion(res.data.suggestion);
    setLoading(false);
  }

  async function copy() {
    if (!suggestion) return;
    await navigator.clipboard.writeText(suggestion.command).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <section className="panel-card">
      <h3>{tr("desktopHelp")}</h3>
      <p className="section-desc">{tr("desktopHelpDesc")}</p>
      <div className="jarvis-help-row">
        <div className="ss-toggle" role="group">
          <button type="button" className={shell === "powershell" ? "active" : ""} onClick={() => setShell("powershell")}>
            PowerShell
          </button>
          <button type="button" className={shell === "bash" ? "active" : ""} onClick={() => setShell("bash")}>
            Bash
          </button>
        </div>
      </div>
      <textarea
        className="jarvis-help-input"
        placeholder={tr("helpPrompt")}
        value={prompt}
        rows={2}
        onChange={(e) => setPrompt(e.target.value)}
      />
      <button type="button" className="ss-login-submit" disabled={loading} onClick={() => void explain()}>
        {tr("explain")}
      </button>

      {suggestion ? (
        <div className="jarvis-suggestion">
          <div className="row">
            <strong>{tr("suggestion")}</strong>
            <button type="button" className="ss-btn-ghost" onClick={() => void copy()}>
              {copied ? tr("copied") : tr("copy")}
            </button>
          </div>
          <pre className="jarvis-cmd">{suggestion.command}</pre>
          <p>
            <strong>{tr("explanation")}:</strong> {suggestion.explanation}
          </p>
          <p className={`jarvis-risk risk-${suggestion.risk}`}>
            <strong>{tr("riskNote")}:</strong> {suggestion.riskNote}
          </p>
          <div className="msg info">{tr("executeDisabled")}</div>
        </div>
      ) : null}
    </section>
  );
}
